import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type NotificationPreference, notificationsAPI } from '../../../lib/api';

export function useNotificationPreferences(organizationId: string | undefined) {
  return useQuery({
    queryKey: ['notifications', 'preferences', organizationId],
    queryFn: async () => {
      if (!organizationId) return [];
      const { data } = await notificationsAPI.getPreferences(organizationId);
      return data;
    },
    enabled: !!organizationId,
    staleTime: 5 * 60_000,
  });
}

export function useUpdatePreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organizationId,
      preferences,
    }: {
      organizationId: string;
      preferences: NotificationPreference[];
    }) => notificationsAPI.updatePreferences(organizationId, preferences),
    onMutate: async ({ organizationId, preferences }) => {
      await queryClient.cancelQueries({
        queryKey: ['notifications', 'preferences', organizationId],
      });

      const previous = queryClient.getQueryData<NotificationPreference[]>([
        'notifications',
        'preferences',
        organizationId,
      ]);

      queryClient.setQueryData(['notifications', 'preferences', organizationId], preferences);

      return { previous, organizationId };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          ['notifications', 'preferences', context.organizationId],
          context.previous,
        );
      }
    },
    onSettled: (_data, _error, { organizationId }) => {
      queryClient.invalidateQueries({
        queryKey: ['notifications', 'preferences', organizationId],
      });
    },
  });
}
